<template>
  <DialogBase
    :visible.sync="visible"
    :title="dialogTitle"
    size="lg"
    :confirm-loading="loading"
    confirm-text="Lưu nhận xét"
    cancel-text="Đóng"
    :use-panel="true"
    panel-title="Nhận xét tháng"
    panel-subtitle="Tạo hoặc chỉnh sửa nhận xét tổng hợp cuối tháng cho học sinh"
    panel-icon="el-icon-notebook-2"
    panel-icon-tone="warning"
    @confirm="handleSave"
    @cancel="handleClose"
  >
    <el-form
      ref="dataForm"
      :model="dataForm"
      :rules="formRules"
      label-position="top"
      class="sk-u-mt-12"
    >
      <div class="sk-form-grid">
        <el-form-item label="Học sinh" prop="studentId">
          <el-select
            v-model="dataForm.studentId"
            placeholder="Chọn học sinh"
            filterable
            style="width: 100%"
            disabled
            @change="handleStudentChange"
          >
            <el-option
              v-for="item in studentList"
              :key="item.studentId"
              :label="item.studentName"
              :value="item.studentId"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Tháng" prop="date">
          <el-date-picker
            v-model="dataForm.date"
            type="month"
            value-format="yyyy-MM"
            format="MM/yyyy"
            placeholder="Chọn tháng"
            :clearable="false"
            style="width: 200px"
            disabled
          />
        </el-form-item>
      </div>

      <el-form-item label="Nội dung" prop="content">
        <el-input
          v-model="dataForm.content"
          type="textarea"
          :rows="6"
          placeholder="Nhập nội dung nhận xét hoặc chọn mẫu ở trên..."
          maxlength="2000"
          show-word-limit
        />
      </el-form-item>

      <!-- File/ảnh đính kèm (Ở DƯỚI CÙNG SAU CÁC NHẬN XÉT) -->
      <div class="evl-upload-section">
        <div class="evl-upload-section__header">
          <span class="evl-upload-title">
            <i class="el-icon-paperclip" /> File & hình ảnh đính kèm
          </span>
          <span class="evl-upload-subtitle">Tối đa 3 file/nhận xét (Ảnh, Word, Excel hoặc PDF)</span>
        </div>
        <el-upload
          drag
          action="#"
          :auto-upload="false"
          :file-list="fileList"
          :limit="3"
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
          multiple
          :on-change="handleFileChange"
          :on-remove="handleFileRemove"
          :on-exceed="handleFileExceed"
          class="evl-drag-uploader"
        >
          <i class="el-icon-upload" />
          <div class="el-upload__text">
            Kéo thả file/ảnh vào đây hoặc <em>nhấn để chọn file</em>
          </div>
        </el-upload>
      </div>
    </el-form>
  </DialogBase>
</template>

<script>
import DialogBase from "@/components/DialogBase.vue";
import EvaluationService from "@/modules/evaluation/services/EvaluationService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import moment from "moment";
import { EVALUATION_FILE_MAX_COUNT, isEvaluationFileAllowed } from "@/modules/evaluation/utils/evaluationFile";

export default {
  name: "EvaluationMonthlyFormDialog",
  components: { DialogBase },
  props: {
    classId: { type: [Number, String], default: "" },
    date: { type: String, default: "" },
    studentList: { type: Array, default: () => [] },
  },
  data() {
    return {
      visible: false,
      loading: false,
      isEditMode: false,
      dataForm: {
        studentId: "",
        studentName: "",
        date: "",
        category: "",
        content: "",
      },
      formRules: {
        studentId: [{ required: true, message: "Vui lòng chọn học sinh", trigger: "change" }],
        date: [{ required: true, message: "Vui lòng chọn tháng", trigger: "change" }],
        category: [{ required: false, message: "Vui lòng chọn danh mục", trigger: "change" }],
        content: [
          { required: true, message: "Vui lòng nhập nội dung", trigger: "blur" },
          { max: 2000, message: "Tối đa 2000 ký tự", trigger: "blur" },
        ],
      },
      templateLoading: false,
      originalContent: "",
      originalFiles: [],
      fileList: [],
    };
  },
  computed: {
    dialogTitle() {
      return this.isEditMode ? "Chỉnh sửa nhận xét tháng" : "Tạo nhận xét tháng";
    },
    schoolId() {
      return this.$store.state.auth.user.schoolId;
    },
    teacherId() {
      return this.$store.state.auth.user.id;
    },
    teacherName() {
      return this.$store.state.auth.user.fullName;
    },
  },
  methods: {
    openForCreate(student) {
      this.isEditMode = false;
      this._resetForm();
      this.dataForm.date = this.date ? this.date.substring(0, 7) : "";
      if (student) {
        this.dataForm.studentId = student.studentId;
        this.dataForm.studentName = student.studentName;
      }
      this.visible = true;
    },

    openForEdit(rowData) {
      this.isEditMode = true;
      this._resetForm();
      this.dataForm.studentId = rowData.studentId;
      this.dataForm.studentName = rowData.studentName;
      this.dataForm.date = rowData.evaluation.date ? rowData.evaluation.date.substring(0, 7) : (this.date ? this.date.substring(0, 7) : "");
      this.dataForm.category = rowData.evaluation.category || "";
      this.dataForm.content = rowData.evaluation.content;
      this.originalContent = this.normalizeContent(this.dataForm.content);
      this.fileList = (rowData.evaluation.files || []).map((file, index) => ({
        uid: `existing-${index}`,
        name: String(file).split("/").pop(),
        existing: true,
        objectKey: file,
        status: "success",
      }));
      this.originalFiles = this.getFileState();
      this.visible = true;
    },

    async handleSave() {
      const valid = await this.$refs.dataForm.validate().catch(() => false);
      if (!valid) return;

      if (this.isEditMode
        && this.normalizeContent(this.dataForm.content) === this.originalContent
        && JSON.stringify(this.getFileState()) === JSON.stringify(this.originalFiles)) {
        NotifyMessage.leftCornerNotificationError("Chưa có thay đổi để lưu!");
        return;
      }

      this.loading = true;
      try {
        const requestDate = moment(this.dataForm.date, "YYYY-MM", true)
          .startOf("month")
          .format("YYYY-MM-DD");
        if (this.isEditMode) {
          const payload = {
            schoolId: this.schoolId,
            classId: this.classId,
            studentId: this.dataForm.studentId,
            date: requestDate,
            content: this.dataForm.content,
            files: this.getFiles(),
            existingFiles: this.getExistingFiles(),
          };
          await EvaluationService.updateMonthly(payload);
          NotifyMessage.leftCornerNotificationSuccess("Cập nhật nhận xét tháng thành công!");
        } else {
          const student = this.studentList.find(s => s.studentId === this.dataForm.studentId);
          const payload = {
            classId: this.classId,
            studentId: this.dataForm.studentId,
            studentName: student ? student.studentName : this.dataForm.studentName,
            date: requestDate,
            category: this.dataForm.category,
            content: this.dataForm.content,
            files: this.getFiles(),
          };
          await EvaluationService.createMonthly(payload);
          NotifyMessage.leftCornerNotificationSuccess("Tạo nhận xét tháng thành công!");
        }
        this.$emit("saved");
        this.handleClose();
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Có lỗi xảy ra!"
        );
      } finally {
        this.loading = false;
      }
    },

    handleClose() {
      this.$refs.dataForm?.resetFields();
      this.visible = false;
      this.templateList = [];
    },

    handleStudentChange(val) {
      const student = this.studentList.find(s => s.studentId === val);
      if (student) this.dataForm.studentName = student.studentName;
    },

    handleFileChange(file, fileList) {
      if (!isEvaluationFileAllowed(file.raw)) {
        NotifyMessage.leftCornerNotificationError("Chỉ được tải ảnh, file Word, Excel hoặc PDF!");
        this.fileList = fileList.filter(item => item.uid !== file.uid);
        return;
      }
      if (fileList.length > EVALUATION_FILE_MAX_COUNT) {
        NotifyMessage.leftCornerNotificationError("Mỗi nhận xét chỉ được đính kèm tối đa 3 file!");
        this.fileList = fileList.slice(0, EVALUATION_FILE_MAX_COUNT);
        return;
      }
      this.fileList = fileList;
    },

    handleFileRemove(file, fileList) {
      this.fileList = fileList;
    },

    handleFileExceed() {
      NotifyMessage.leftCornerNotificationError("Mỗi nhận xét chỉ được đính kèm tối đa 3 file!");
    },

    getFiles() {
      return this.fileList.map(file => file.raw).filter(Boolean);
    },

    getExistingFiles() {
      return this.fileList.filter(file => file.existing).map(file => file.objectKey);
    },

    getFileState() {
      return this.fileList.map(file => file.existing
        ? `existing:${file.objectKey}`
        : `new:${file.name}:${file.size || file.raw?.size || 0}:${file.raw?.lastModified || 0}`
      ).sort();
    },

    _resetForm() {
      this.originalContent = "";
      this.originalFiles = [];
      this.fileList = [];
      this.dataForm = {
        studentId: "",
        studentName: "",
        date: "",
        category: "",
        content: "",
      };
      this.templateList = [];
      this.$nextTick(() => {
        this.$refs.dataForm?.clearValidate();
      });
    },

    normalizeContent(content) {
      return typeof content === "string" ? content.trim() : "";
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog";

.template-picker {
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 6px;
  background: #f8fafc;
}

.template-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  border: 1px solid transparent;
  font-size: 13px;
  color: #475569;
  line-height: 1.5;

  i {
    margin-top: 2px;
    flex-shrink: 0;
    color: #94a3b8;
  }

  &:hover {
    background: #eff6ff;
    border-color: #dbeafe;
  }
}

.template-loading-hint {
  color: #64748b;
  font-size: 13px;
  i { margin-right: 4px; }
}

/* ─── Modern Drag Dropzone ───────────────────────── */
.evl-upload-section {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px dashed #e2e8f0;
}

.evl-upload-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.evl-upload-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 6px;

  i {
    color: #2563eb;
    font-size: 15px;
  }
}

.evl-upload-subtitle {
  font-size: 12px;
  color: #94a3b8;
}

.evl-drag-uploader {
  width: 100%;

  /deep/ .el-upload {
    width: 100%;
  }

  /deep/ .el-upload-dragger {
    width: 100%;
    height: 100px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 2px dashed #cbd5e1;
    border-radius: 12px;
    background: #f8fafc;
    transition: all 0.2s ease;

    &:hover {
      border-color: #3b82f6;
      background: #eff6ff;
    }

    .el-icon-upload {
      font-size: 32px;
      color: #3b82f6;
      margin: 0 0 2px;
      line-height: 1;
    }

    .el-upload__text {
      font-size: 13px;
      color: #64748b;
      line-height: 1.4;

      em {
        color: #2563eb;
        font-weight: 600;
        font-style: normal;
      }
    }
  }

  /deep/ .el-upload-list {
    margin-top: 8px;

    .el-upload-list__item {
      border-radius: 8px;
      background: #f1f5f9;
      border: 1px solid #e2e8f0;
      margin-top: 6px;
      transition: all 0.15s ease;

      &:hover {
        background: #e2e8f0;
      }
    }
  }
}
</style>
